import { useState } from "react";
import { ShieldCheck, ArrowLeft, BadgeCheck, Lock, Mail, Loader2 } from "lucide-react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { googleLogin } from "@/components/common";
import { useAuth } from "@/context/AuthContext";

function formatErr(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function Login() {
  const { user, loading, setUser } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  if (!loading && user) {
    const to = user.roles?.includes("admin") ? "/admin" : user.roles?.includes("agency") && user.active_role === "agency" ? "/agency" : "/dashboard";
    return <Navigate to={to} replace />;
  }

  const routeFor = (u) => u.roles?.includes("admin") ? "/admin" : u.roles?.includes("agency") && u.active_role === "agency" ? "/agency" : "/dashboard";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      const payload = mode === "login" ? { email: f.email, password: f.password } : { name: f.name, email: f.email, password: f.password };
      const { data } = await api.post(path, payload);
      setUser(data);
      toast.success(mode === "login" ? "Welcome back!" : "Account created!");
      navigate(routeFor(data));
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-navy-900 text-white p-12 relative overflow-hidden">
        <div className="absolute inset-0 hero-grid opacity-[0.06]" />
        <Link to="/" className="relative z-10">
          <img src="/guarddekho-logo.webp" alt="GuardDekho.com" className="h-20 w-auto rounded-xl bg-white px-4 py-3" />
        </Link>
        <div className="relative z-10">
          <h1 className="font-heading text-4xl font-extrabold tracking-tighter leading-tight">One platform for<br />professional security.</h1>
          <p className="mt-4 text-navy-300 max-w-md">Find, compare, hire and manage verified security agencies, guards and technology — all in one place.</p>
          <div className="mt-8 space-y-3">
            {["PSARA & GST verified agencies", "Transparent quotation marketplace", "Digital contracts & attendance"].map((t) => (
              <div key={t} className="flex items-center gap-2 text-sm text-navy-200"><BadgeCheck size={16} className="text-brand" /> {t}</div>
            ))}
          </div>
        </div>
        <p className="relative z-10 text-xs text-navy-500">Secured with encrypted authentication · Role-based access</p>
      </div>

      <div className="flex flex-col items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <Link to="/" className="inline-flex items-center gap-1 text-sm text-navy-500 hover:text-brand mb-8"><ArrowLeft size={16} /> Back to home</Link>
          <h2 className="font-heading text-3xl font-bold text-navy-900 tracking-tight">{mode === "login" ? "Welcome back" : "Create your account"}</h2>
          <p className="mt-2 text-navy-500">{mode === "login" ? "Sign in to your GuardDekho account." : "Join GuardDekho in a few seconds."}</p>

          <form onSubmit={submit} className="mt-6 space-y-3">
            {mode === "register" && (
              <div>
                <Label className="text-xs font-semibold text-navy-600">Full Name</Label>
                <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Your name" required data-testid="auth-name" className="mt-1" />
              </div>
            )}
            <div>
              <Label className="text-xs font-semibold text-navy-600">Email</Label>
              <Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="you@example.com" required data-testid="auth-email" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-navy-600">Password</Label>
              <Input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} placeholder="••••••••" required minLength={6} data-testid="auth-password" className="mt-1" />
            </div>
            <Button type="submit" className="w-full h-11 bg-brand hover:bg-brand-600 font-semibold" disabled={busy} data-testid="auth-submit-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (mode === "login" ? "Sign In" : "Create Account")}
            </Button>
          </form>

          <p className="mt-3 text-sm text-navy-500 text-center">
            {mode === "login" ? "New to GuardDekho? " : "Already have an account? "}
            <button onClick={() => setMode(mode === "login" ? "register" : "login")} className="text-brand font-semibold hover:underline" data-testid="auth-toggle-btn">
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </p>

          <div className="my-5 flex items-center gap-3 text-xs text-navy-400"><span className="flex-1 h-px bg-navy-200" /> OR <span className="flex-1 h-px bg-navy-200" /></div>

          <Button className="w-full h-12 bg-white border border-navy-200 text-navy-900 hover:bg-navy-50 font-semibold" onClick={googleLogin} data-testid="google-login-btn">
            <img src="https://www.svgrepo.com/show/475656/google-color.svg" alt="" className="w-5 h-5 mr-2" /> Continue with Google
          </Button>
          <div className="mt-6 flex items-start gap-2 text-xs text-navy-500 bg-navy-50 border border-navy-200 rounded-lg p-3">
            <Lock size={14} className="mt-0.5 shrink-0 text-brand" />
            <span>New customers get a Customer account automatically. <Link to="/agency/register" className="text-brand font-semibold hover:underline">Register your agency</Link> to access the Agency portal.</span>
          </div>
          <p className="mt-6 text-xs text-navy-400 text-center">By continuing you agree to our <Link to="/legal/terms" className="underline">Terms</Link> & <Link to="/legal/privacy" className="underline">Privacy Policy</Link>.</p>
        </div>
      </div>
    </div>
  );
}
